import {
  createElement,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
} from "react";
import { useInView } from "@/shared/hooks/useInView";
import styles from "./Reveal.module.css";

type RevealProps = {
  children: ReactNode;
  /** Elemento renderizado (div por padrão). Ex.: "section", "article". */
  as?: ElementType;
  /** Atraso em ms para efeito escalonado (stagger) entre itens. */
  delay?: number;
  /**
   * Anima só uma vez (true, padrão) ou reverte ao sair do viewport, reanimando
   * ao voltar (false). Com once, o observer desconecta após a descoberta.
   */
  once?: boolean;
  className?: string;
} & Omit<ComponentPropsWithoutRef<"div">, "className" | "children">;

/**
 * Envolve qualquer conteúdo com um fade + subida suave quando entra no viewport.
 * Anima apenas opacity/transform (compostas na GPU), respeita prefers-reduced-motion
 * e desconecta o observer após disparar — custo zero depois de aparecer.
 */
export function Reveal({
  children,
  as = "div",
  delay = 0,
  once = true,
  className,
  ...rest
}: RevealProps) {
  const { ref, inView } = useInView<HTMLElement>({ once });

  return createElement(
    as,
    {
      ref,
      className: [styles.reveal, inView ? styles.visible : "", className]
        .filter(Boolean)
        .join(" "),
      style: delay ? { transitionDelay: `${delay}ms` } : undefined,
      ...rest,
    },
    children,
  );
}
